import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, TableSortLabel, Checkbox, FormControlLabel, Typography, Button, Tooltip, useTheme, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';

export interface MapListEntry {
  id: string;
  name: string; // Company name or Address text
  organizationNumber?: string;
  type: 'Company' | 'Address';
  connectionCount: number;
  next_layer_company_count?: number;
  coordinates: [number, number] | null;
  isHidden?: boolean;
}

export interface HistoryEntry {
  addressText: string;
  startDate?: string;
  endDate?: string;
  coordinates: [number, number];
  isCurrent: boolean;
}

interface CompanyMapSidePanelProps {
  companies: MapListEntry[];
  addresses: MapListEntry[];
  history?: HistoryEntry[];
  onSelectLocation: (coordinates: [number, number]) => void;
  onHoverLocation?: (coordinates: [number, number] | null) => void;
  selectedEntity: { id: string, type: 'Company' | 'Address' } | null;
  onSelectEntity: (entity: { id: string, type: 'Company' | 'Address' } | null) => void;
  filterEnabled: boolean;
  onToggleFilter: (enabled: boolean) => void;
  showConnections: boolean;
  onToggleShowConnections: (enabled: boolean) => void;
  onGenerateAISummary?: () => void;
  isCompact?: boolean;
}

type SortKey = 'name' | 'count';

interface SortConfig {
  key: SortKey;
  direction: 'asc' | 'desc';
}

const CompanyMapSidePanel: React.FC<CompanyMapSidePanelProps> = ({ 
  companies, 
  addresses, 
  onSelectLocation,
  onHoverLocation,
  selectedEntity,
  onSelectEntity,
  filterEnabled,
  onToggleFilter,
  showConnections,
  onToggleShowConnections,
  onGenerateAISummary,
  history,
  isCompact = false
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [tabValue, setTabValue] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  const [companySearch, setCompanySearch] = useState('');
  const [addressSearch, setAddressSearch] = useState('');
  const matchingCompanies = useMemo(() => {
    const query = companySearch.trim().toLocaleLowerCase(i18n.language);
    if (!query) return companies;
    const numberQuery = /^[\d\s-]+$/.test(query) ? query.replace(/\D/g, '') : '';
    return companies.filter(company =>
      company.name.toLocaleLowerCase(i18n.language).includes(query)
      || (numberQuery && company.organizationNumber?.replace(/\D/g, '').includes(numberQuery)),
    );
  }, [companies, companySearch, i18n.language]);

  const matchingAddresses = useMemo(() => {
    const terms = addressSearch.trim().toLocaleLowerCase(i18n.language).split(/\s+/).filter(Boolean);
    if (!terms.length) return addresses;
    return addresses.filter(address => {
      const text = address.name.toLocaleLowerCase(i18n.language).replace(/\s+/g, '');
      return terms.every(term => text.includes(term));
    });
  }, [addresses, addressSearch, i18n.language]);

  const selectEntry = (entry: MapListEntry) => {
    onSelectEntity({ id: entry.id, type: entry.type });
    if (entry.coordinates) onSelectLocation(entry.coordinates);
  };

  const searchValue = tabValue === 0 ? companySearch : addressSearch;
  const setSearchValue = tabValue === 0 ? setCompanySearch : setAddressSearch;
  const searchMatches = tabValue === 0 ? matchingCompanies : matchingAddresses;

  useEffect(() => {
    if (!history && tabValue === 2) {
      setTabValue(0);
    }
  }, [history, tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const colors = {
    background: theme.palette.background.paper,
    headerBackground: isDarkMode ? '#1e293b' : '#f8fafc',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    textPrimary: isDarkMode ? '#f1f5f9' : '#0f172a',
    textSecondary: isDarkMode ? '#e2e8f0' : '#334155',
    hover: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
    shadow: isDarkMode ? '0px 4px 20px rgba(0, 0, 0, 0.5)' : '0px 4px 20px rgba(0, 0, 0, 0.1)',
    sortLabel: isDarkMode ? '#e2e8f0' : '#334155',
    sortLabelHover: isDarkMode ? '#fff' : '#000',
    icon: isDarkMode ? '#94a3b8' : '#64748b',
    noData: isDarkMode ? '#94a3b8' : '#64748b',
    tabText: isDarkMode ? '#94a3b8' : '#64748b',
    selectedRow: isDarkMode ? 'rgba(56, 189, 248, 0.1)' : 'rgba(56, 189, 248, 0.1)', // Keep same for now or adjust
    timelineLine: isDarkMode ? '#334155' : '#e2e8f0',
    timelineDotCurrent: '#22c55e',
    timelineDotPast: isDarkMode ? '#64748b' : '#94a3b8',
    timelineDotBorder: theme.palette.background.paper,
    paperBg: isDarkMode ? '#1e293b' : '#f8fafc',
    paperBorder: isDarkMode ? '#334155' : '#e2e8f0',
    paperHoverBg: isDarkMode ? '#28364a' : '#f1f5f9',
    currentText: '#22c55e',
    checkbox: isDarkMode ? '#94a3b8' : '#64748b',
    checkboxChecked: '#38bdf8',
    buttonText: '#38bdf8',
    buttonBorder: '#38bdf8',
    buttonHoverBg: 'rgba(56, 189, 248, 0.1)',
  };

  // Styles matching HoverDataUtils.css
  const tableStyles = {
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    overflow: 'auto',
    boxShadow: colors.shadow,
  };

  const headerStyles = {
    backgroundColor: colors.headerBackground,
    color: colors.textSecondary,
    fontWeight: 600,
    fontSize: 'var(--font-size-xs)',
    borderBottom: `1px solid ${colors.border}`,
    padding: isCompact ? 'var(--spacing-xs)' : 'var(--spacing-sm)',
    fontFamily: 'var(--font-family)',
  };

  const cellStyles = {
    color: colors.textPrimary,
    fontSize: 'var(--font-size-xs)',
    borderBottom: `1px solid ${colors.border}`,
    padding: isCompact ? '6px' : 'var(--spacing-xs)',
    fontFamily: 'var(--font-family)',
    cursor: 'pointer',
  };

  const rowHoverStyles = {
    '&:hover': {
      backgroundColor: colors.hover,
    },
    '&:last-child td': {
      borderBottom: 'none',
    }
  };

  const renderTable = (data: MapListEntry[], countHeader: string) => {
    const sortedData = [...data].sort((a, b) => {
      let comparison = 0;
      if (sortConfig.key === 'name') {
        comparison = a.name.localeCompare(b.name, i18n.language);
      } else {
        const getCount = (entry: MapListEntry) => 
          entry.type === 'Company' && entry.next_layer_company_count !== undefined 
            ? entry.next_layer_company_count 
            : entry.connectionCount;
        comparison = getCount(a) - getCount(b);
      }
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return (
    <TableContainer component={Paper} sx={{ ...tableStyles, flex: 1, height: '100%', backgroundColor: colors.background }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerStyles}>
              <TableSortLabel
                active={sortConfig.key === 'name'}
                direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('name')}
                sx={{
                  '&.MuiTableSortLabel-root': { color: colors.sortLabel },
                  '&.MuiTableSortLabel-root:hover': { color: colors.sortLabelHover },
                  '&.Mui-active': { color: '#3b82f6' },
                  '& .MuiTableSortLabel-icon': { color: '#3b82f6 !important' },
                }}
              >
                {t('common.fields.name')}
              </TableSortLabel>
            </TableCell>
            {!isCompact && (
            <TableCell align="center" sx={{ ...headerStyles, width: '80px' }}>
              <TableSortLabel
                active={sortConfig.key === 'count'}
                direction={sortConfig.key === 'count' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('count')}
                sx={{
                  '&.MuiTableSortLabel-root': { color: colors.sortLabel },
                  '&.MuiTableSortLabel-root:hover': { color: colors.sortLabelHover },
                  '&.Mui-active': { color: '#3b82f6' },
                  '& .MuiTableSortLabel-icon': { color: '#3b82f6 !important' },
                }}
              >
                {countHeader}
              </TableSortLabel>
            </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row) => (
            <TableRow 
              key={row.id} 
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  selectEntry(row);
                }
              }}
              sx={{
                  ...rowHoverStyles,
                  backgroundColor: (selectedEntity?.id === row.id && selectedEntity?.type === row.type) ? colors.selectedRow : 'inherit'
              }}
              onClick={() => {
                  selectEntry(row);
              }}
            >
              <TableCell sx={cellStyles}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ marginRight: '8px' }}>{row.name}</span>
                        {row.isHidden && (
                            <Tooltip title={t('graph.map.hidden')}>
                                <VisibilityOffIcon sx={{ fontSize: 16, color: colors.icon }} />
                            </Tooltip>
                        )}
                    </div>
                    {row.coordinates && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelectLocation(row.coordinates!);
                            }}
                            title={t('graph.map.zoom')}
                            sx={{ 
                                color: colors.icon, 
                                padding: '4px',
                                '&:hover': { color: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.1)' } 
                            }}
                        >
                            <CenterFocusStrongIcon sx={{ fontSize: isCompact ? '14px' : '16px' }} />
                        </IconButton>
                    )}
                </Box>
              </TableCell>
              {!isCompact && (
              <TableCell align="center" sx={cellStyles}>
                {row.type === 'Company' && row.next_layer_company_count !== undefined ? row.next_layer_company_count : row.connectionCount}
              </TableCell>
              )}
            </TableRow>
          ))}
          {sortedData.length === 0 && (
            <TableRow>
              <TableCell colSpan={isCompact ? 1 : 2} sx={{ ...cellStyles, textAlign: 'center', color: colors.noData }}>
                {t(searchValue.trim() ? (tabValue === 0 ? 'graph.map.noMatchingCompanies' : 'graph.map.noMatchingAddresses') : 'graph.noData')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
  };

  const renderHistory = (history: HistoryEntry[]) => {
    return (
      <Box sx={{ p: 2, overflowY: 'auto', flex: 1, height: '100%' }}>
        <div style={{ position: 'relative', paddingLeft: '20px' }}>
          {/* Vertical line */}
          <div style={{ 
            position: 'absolute', 
            left: '7px', 
            top: '10px', 
            bottom: '10px', 
            width: '2px', 
            backgroundColor: colors.timelineLine 
          }} />
          
          {history.map((entry, index) => (
            <Box key={index} sx={{ mb: 3, position: 'relative' }}>
              {/* Dot on the timeline */}
              <div style={{ 
                position: 'absolute', 
                left: '-24px', 
                top: '6px', 
                width: '16px', 
                height: '16px', 
                borderRadius: '50%', 
                backgroundColor: entry.isCurrent ? colors.timelineDotCurrent : colors.timelineDotPast,
                border: `3px solid ${colors.timelineDotBorder}`,
                zIndex: 1
              }} />
              
              <Paper sx={{ 
                p: 2, 
                backgroundColor: colors.paperBg, 
                border: entry.isCurrent ? `1px solid ${colors.timelineDotCurrent}` : `1px solid ${colors.paperBorder}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  borderColor: '#38bdf8',
                  transform: 'translateX(4px)',
                  backgroundColor: colors.paperHoverBg
                }
              }}
              onClick={() => onSelectLocation(entry.coordinates)}
              onMouseEnter={() => onHoverLocation && onHoverLocation(entry.coordinates)}
              onMouseLeave={() => onHoverLocation && onHoverLocation(null)}
              >
                <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: 0.5, fontWeight: 600 }}>
                  {entry.startDate || t('common.states.unknown')} — {entry.endDate || (entry.isCurrent ? t('common.states.present') : t('common.states.unknown'))}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                  {entry.addressText}
                </Typography>
                {entry.isCurrent && (
                  <Typography variant="caption" sx={{ color: colors.currentText, mt: 1, display: 'block', fontWeight: 700 }}>
                    {t('graph.map.currentAddress')}
                  </Typography>
                )}
              </Paper>
            </Box>
          ))}
        </div>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      width: isExpanded ? (isCompact ? 250 : 450) : 50, 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: colors.background, // Match table bg for seamless look
      borderRight: `1px solid ${colors.border}`,
      zIndex: 1000,
      transition: 'width 0.3s ease',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        p: 1, 
        display: 'flex', 
        justifyContent: isExpanded ? 'flex-end' : 'center',
        borderBottom: isExpanded ? `1px solid ${colors.border}` : 'none'
      }}>
        <IconButton 
          onClick={() => setIsExpanded(!isExpanded)} 
          size="small" 
          sx={{ color: colors.icon }}
        >
          {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>

      {isExpanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minWidth: isCompact ? 250 : 450 }}>
          <Box sx={{ p: isCompact ? 1 : 2, borderBottom: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant={isCompact ? "subtitle2" : "h6"} sx={{ color: colors.textPrimary }}>
              {t('graph.map.details')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <FormControlLabel
                  control={
                      <Checkbox
                          checked={filterEnabled}
                          onChange={(e) => onToggleFilter(e.target.checked)}
                          sx={{ 
                            color: colors.checkbox, 
                            '&.Mui-checked': { color: colors.checkboxChecked }, 
                            padding: isCompact ? '4px' : '6px' 
                          }}
                          size={isCompact ? "small" : "medium"}
                      />
                  }
                  label={<Typography sx={{ color: colors.textSecondary, fontSize: isCompact ? '0.75rem' : '0.875rem' }}>{t('graph.map.filterSelection')}</Typography>}
                  sx={{ ml: -0.5 }}
              />

              {onGenerateAISummary && (
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={onGenerateAISummary}
                  sx={{ 
                    mt: 1, 
                    color: colors.buttonText, 
                    borderColor: colors.buttonBorder,
                    textTransform: 'none',
                    '&:hover': { borderColor: '#7dd3fc', backgroundColor: colors.buttonHoverBg }
                  }}
                >
                  {t('graph.map.generateSummary')}
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: colors.border }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  color: colors.tabText,
                  fontFamily: 'var(--font-family)',
                  fontSize: isCompact ? 'var(--font-size-xs)' : 'var(--font-size-small)',
                  fontWeight: 600,
                  minHeight: isCompact ? '36px' : '48px',
                  padding: isCompact ? '6px 12px' : '12px 16px',
                  '&.Mui-selected': {
                    color: '#3b82f6',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#3b82f6',
                }
              }}
            >
              <Tab label={t('graph.companiesWithCount', { count: companies.length })} />
              <Tab label={t('graph.addressesWithCount', { count: addresses.length })} />
              {history && <Tab label={t('graph.map.history')} />}
            </Tabs>
          </Box>
          
          {tabValue !== 2 && (
            <Box sx={{ p: isCompact ? 1 : 2 }}>
              <TextField
                fullWidth size="small"
                label={t(tabValue === 0 ? 'graph.map.searchCompanies' : 'graph.map.searchAddresses')}
                placeholder={t(tabValue === 0 ? 'graph.map.searchCompanyPlaceholder' : 'graph.map.searchAddressPlaceholder')}
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                onKeyDown={(event) => {
                  if (!(event.target instanceof HTMLInputElement)) return;
                  if (event.key === 'Enter' && searchMatches.length === 1) {
                    event.preventDefault();
                    selectEntry(searchMatches[0]);
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: searchValue && <InputAdornment position="end">
                    <IconButton size="small" aria-label={t(tabValue === 0 ? 'graph.map.clearCompanySearch' : 'graph.map.clearAddressSearch')} onClick={() => setSearchValue('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>,
                }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, overflow: 'hidden', p: isCompact ? 0.5 : 1, display: 'flex', flexDirection: 'column' }}>
            {tabValue === 0 && renderTable(matchingCompanies, t('graph.map.nextLayerShort'))}
            {tabValue === 1 && renderTable(matchingAddresses, t('graph.map.directShort'))}
            {tabValue === 2 && history && renderHistory(history)}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CompanyMapSidePanel;
